import {
  betragAnteilEinzelpreis,
  calcPosition,
  calcRowForPosition,
  type KalkPreisbezug,
  type PositionTotals,
} from "@/lib/documentCalc";
import type { ArtikelNachkalkulationJson, DokumentPosition, GdLohnart, PositionTyp } from "@/lib/dokumente";
import {
  normalizeAufschlagModus,
  pricingFromDatabase,
  roundMoney,
  syncPricingAufschlagModus,
  syncPricingField,
  type ArticlePricingFields,
  type AufschlagModus,
  type PricingDriver,
  vkFromEk,
} from "@/pages/dokumente/documentPricing";
import {
  calcLeistungKalkuliertNetto,
  resolveLeistungVkNetto,
  syncLeistungVkDriverOnKalkulationChange,
} from "@/pages/dokumente/documentLeistungPricing";
import {
  DEFAULT_GEBUCHTE_STUNDEN_HINWEIS,
  DEFAULT_NACHWEIS_HINWEIS,
  POSITIONSART_LABELS,
  readGdMeta,
  readLieferscheinDelivery,
  writeGdMeta,
  type GdPositionMeta,
  type GdPositionsart,
  type GdPreisart,
  type MergedChildSnapshot,
} from "@/pages/dokumente/gdPositionMeta";

export {
  vkFromEk,
  DEFAULT_NACHWEIS_HINWEIS,
  DEFAULT_GEBUCHTE_STUNDEN_HINWEIS,
  POSITIONSART_LABELS,
  readLieferscheinDelivery,
};
export type { GdPreisart, GdPositionsart };

export type BlockPositionTyp = "artikel" | "leistung" | "titel" | "text";
/** Palette-Bausteine inkl. Nachweisarbeiten (persistiert als Artikel + Meta). */
export type PaletteBlockTyp = BlockPositionTyp | "nachweisarbeiten";

export interface LeistungArtikelZeile {
  localId: string;
  artikel_id?: number | null;
  ist_freier_artikel?: boolean;
  bezeichnung: string;
  hero_nr?: string | null;
  einheit?: string | null;
  menge: number;
  einzelpreis_netto: number;
  einkaufspreis_netto?: number;
  aufschlag_prozent?: number;
  preis_driver?: PricingDriver;
  rabatt_prozent: number;
  /** einzel = je Stück der Position, gesamt = einmal für die ganze Menge. */
  preisbezug?: KalkPreisbezug;
  interne_beschreibung?: string;
}

export interface LeistungLohnZeile {
  localId: string;
  lohnart_id: string;
  bezeichnung: string;
  stunden: number;
  stundensatz_netto: number;
  /** einzel = Stunden je Stück, gesamt = einmal für die ganze Menge. */
  preisbezug?: KalkPreisbezug;
  interne_beschreibung?: string;
}

export interface LeistungKalkulation {
  artikel_liste: LeistungArtikelZeile[];
  lohn_liste: LeistungLohnZeile[];
  stunden?: number;
  stundensatz_netto?: number;
  pauschale_netto?: number;
  aufschlag_prozent?: number;
  vk_driver?: "kalkuliert" | "pauschale" | "manuell";
}

export interface EditorPosition extends DokumentPosition {
  localId: string;
}

/**
 * Frisch gezogener oder angeklickter Baustein.
 * Liegt erst nach „Übernehmen“ im Dokument; Abbrechen verwirft ihn.
 */
export type ProvisionalPositionDraft = {
  position: EditorPosition;
  insertAt: number;
  titelAssignment: string | null | undefined;
};

export const POSITION_DRAG_TYPE = "application/x-eh-gd-position-block";
export const POSITION_INDEX_DRAG = "application/x-eh-gd-position-index";
export const POSITION_EINKAUF_ARTIKEL_DRAG_TYPE = "application/x-eh-gd-einkauf-artikel";
export const POSITION_GEBUCHTE_STUNDEN_DRAG_TYPE = "application/x-eh-gd-gebuchte-stunden";

export interface GebuchteStundenDragPayload {
  totalHours: number;
}

export interface EinkaufArtikelPaletteItem {
  bezeichnung: string;
  seller_article_nr?: string | null;
  ean?: string | null;
  einheit?: string | null;
  menge?: number | null;
  einzelpreis_netto?: number | null;
  stamm_artikel_id?: number | null;
  stamm_hero_nr?: string | null;
  stamm_artikel_bezeichnung?: string | null;
  lieferant_firmenname?: string | null;
}

export interface EinkaufArtikelDragPayload {
  item: EinkaufArtikelPaletteItem;
  aufschlagProzent: number;
}

export const POSITION_BLOCK_META: Record<
  PaletteBlockTyp,
  { label: string; hint: string; icon: string }
> = {
  artikel: {
    label: "Artikel",
    hint: "Stammdaten-Artikel oder freier Eintrag",
    icon: "📦",
  },
  leistung: {
    label: "Leistung",
    hint: "Kalkulation mit mehreren Artikeln",
    icon: "🛠",
  },
  nachweisarbeiten: {
    label: "Nachweisarbeiten",
    hint: "Stunden-Artikel auf Nachweis, geschätzte Kosten",
    icon: "⏱",
  },
  titel: {
    label: "Titel",
    hint: "Überschrift im Dokument",
    icon: "T",
  },
  text: {
    label: "Text",
    hint: "Fließtext ohne Preis",
    icon: "¶",
  },
};

let localCounter = 0;

export function newLocalId(prefix = "pos"): string {
  localCounter += 1;
  return `${prefix}-${Date.now()}-${localCounter}`;
}

export function normalizePositionTyp(value?: string | null): BlockPositionTyp {
  const raw = (value || "artikel").toLowerCase();
  if (raw === "freitext" || raw === "zwischensumme") return "artikel";
  if (raw === "leistung" || raw === "titel" || raw === "text") return raw;
  return "artikel";
}

export function toEditorPosition(
  row: DokumentPosition,
  index: number,
  preserveLocalId?: string,
): EditorPosition {
  const typ = normalizePositionTyp(row.position_typ);
  let kalkulation_json = row.kalkulation_json ?? defaultKalkulation(row);
  if (typ === "leistung" && kalkulation_json) {
    const kalk = kalkulation_json as LeistungKalkulation;
    kalkulation_json = {
      ...kalk,
      artikel_liste: (kalk.artikel_liste || []).map((item) => ({
        localId: item.localId || newLocalId("art"),
        artikel_id: item.artikel_id,
        ist_freier_artikel: item.ist_freier_artikel,
        bezeichnung: item.bezeichnung || "",
        hero_nr: item.hero_nr,
        einheit: item.einheit,
        menge: Number(item.menge ?? 1),
        einzelpreis_netto: Number(item.einzelpreis_netto ?? 0),
        einkaufspreis_netto: item.einkaufspreis_netto,
        aufschlag_prozent: item.aufschlag_prozent,
        rabatt_prozent: Number(item.rabatt_prozent ?? 0),
        preisbezug: item.preisbezug === "gesamt" ? "gesamt" : "einzel",
        interne_beschreibung: item.interne_beschreibung,
      })),
      lohn_liste: (kalk.lohn_liste || []).map((item) => ({
        localId: item.localId || newLocalId("lohn"),
        lohnart_id: item.lohnart_id || "",
        bezeichnung: item.bezeichnung || "",
        stunden: Number(item.stunden ?? 0),
        stundensatz_netto: Number(item.stundensatz_netto ?? 0),
        preisbezug: item.preisbezug === "gesamt" ? "gesamt" : "einzel",
        interne_beschreibung: item.interne_beschreibung,
      })),
    };
  }
  return {
    ...row,
    localId: preserveLocalId ?? (row.id != null ? `db-${row.id}` : newLocalId()),
    sort_index: row.sort_index ?? index,
    position_typ: typ as PositionTyp,
    menge: Number(row.menge ?? 1),
    rabatt_prozent: Number(row.rabatt_prozent ?? 0),
    rabatt_betrag: Number(row.rabatt_betrag ?? 0),
    rabatt_modus: row.rabatt_modus === "betrag" ? "betrag" : "prozent",
    rabatt_ausweis: row.rabatt_ausweis === "dokument" ? "dokument" : "intern",
    kalkulation_json,
  };
}

export function mergePositionsAfterSave(
  previous: EditorPosition[],
  saved: DokumentPosition[],
): EditorPosition[] {
  // Bei Längenwechsel (z. B. Merge während Autosave) keine localIds per Index übernehmen –
  // sonst erbt eine Einzelposition die localId der Zusammenfassung und das Merge-Modal greift nicht.
  const lengthMatches = previous.length === saved.length;
  const next = saved.map((p, idx) => {
    const byId = p.id != null ? previous.find((x) => x.id === p.id) : undefined;
    const byIndex = lengthMatches ? previous[idx] : undefined;
    const preserveLocalId = byId?.localId ?? byIndex?.localId;
    return toEditorPosition(p, idx, preserveLocalId);
  });

  const withFallback = next.map((row, idx) => {
    const preserveById = row.id != null ? previous.find((x) => x.id === row.id) : undefined;
    const preserve = preserveById ?? (lengthMatches ? previous[idx] : undefined);
    const prevMeta = preserve ? getPositionMeta(preserve) : {};
    const savedMeta = getPositionMeta(row);
    if (!prevMeta.struktur_titel_local_id) return row;

    const titelStillExists = next.some((p) => p.localId === prevMeta.struktur_titel_local_id);
    const savedHasGroup =
      savedMeta.struktur_titel_local_id ||
      savedMeta.struktur_titel_position_id != null ||
      savedMeta.struktur_titel_sort_index != null;
    if (titelStillExists && !savedHasGroup) {
      return patchPositionMeta(row, resolveTitelReference(prevMeta.struktur_titel_local_id, next));
    }
    return row;
  });

  return linkStrukturTitelLocalIds(withFallback);
}

function resolveTitelReference(
  titelLocalId: string | null | undefined,
  all: EditorPosition[],
): Pick<GdPositionMeta, "struktur_titel_local_id" | "struktur_titel_sort_index" | "struktur_titel_position_id"> {
  if (!titelLocalId) {
    return {
      struktur_titel_local_id: null,
      struktur_titel_sort_index: null,
      struktur_titel_position_id: null,
    };
  }
  const titelRow = all.find((p) => p.localId === titelLocalId);
  if (!titelRow) {
    return { struktur_titel_local_id: titelLocalId };
  }
  const sortIndex = all.indexOf(titelRow);
  return {
    struktur_titel_local_id: titelLocalId,
    struktur_titel_sort_index: sortIndex >= 0 ? sortIndex : null,
    struktur_titel_position_id: titelRow.id ?? null,
  };
}

function findTitelBySortIndex(positions: EditorPosition[], sortIndex: number): EditorPosition | undefined {
  const byArrayIndex = positions[sortIndex];
  if (byArrayIndex && normalizePositionTyp(byArrayIndex.position_typ) === "titel") return byArrayIndex;
  return positions.find(
    (p) => normalizePositionTyp(p.position_typ) === "titel" && p.sort_index === sortIndex,
  );
}

export function linkStrukturTitelLocalIds(positions: EditorPosition[]): EditorPosition[] {
  const idToLocal = new Map<number, string>();
  for (const row of positions) {
    if (row.id != null) idToLocal.set(row.id, row.localId);
  }
  return positions.map((row) => {
    const meta = getPositionMeta(row);
    if (meta.struktur_titel_local_id) {
      const titelRow = positions.find((p) => p.localId === meta.struktur_titel_local_id);
      if (titelRow) return row;
    }

    if (meta.struktur_titel_sort_index != null) {
      const titelPos = findTitelBySortIndex(positions, meta.struktur_titel_sort_index);
      if (titelPos) {
        return patchPositionMeta(row, {
          struktur_titel_local_id: titelPos.localId,
          struktur_titel_sort_index: titelPos.sort_index ?? meta.struktur_titel_sort_index,
          struktur_titel_position_id: titelPos.id ?? null,
        });
      }
    }

    if (meta.struktur_titel_position_id != null) {
      const titelLocalId = idToLocal.get(meta.struktur_titel_position_id) ?? null;
      if (titelLocalId) {
        const titelPos = positions.find((p) => p.localId === titelLocalId);
        return patchPositionMeta(row, {
          struktur_titel_local_id: titelLocalId,
          struktur_titel_sort_index: titelPos?.sort_index ?? meta.struktur_titel_sort_index ?? null,
        });
      }
    }

    return row;
  });
}

export function getPositionMeta(row: EditorPosition): GdPositionMeta {
  return readGdMeta(row.kalkulation_json);
}

export function patchPositionMeta(row: EditorPosition, partial: Partial<GdPositionMeta>): EditorPosition {
  const current = getPositionMeta(row);
  const nextMeta: GdPositionMeta = { ...current, ...partial };
  const typ = normalizePositionTyp(row.position_typ);

  if (typ === "leistung") {
    const kalk = (row.kalkulation_json || { artikel_liste: [], lohn_liste: [] }) as LeistungKalkulation;
    return {
      ...row,
      kalkulation_json: writeGdMeta(kalk, nextMeta) as unknown as LeistungKalkulation,
    };
  }

  if (typ === "artikel") {
    const nachkalk = getArtikelNachkalk(row);
    const merged = writeGdMeta({ ...nachkalk, preis_driver: nachkalk.preis_driver }, nextMeta);
    return { ...row, kalkulation_json: merged as ArtikelNachkalkulationJson | null };
  }

  return {
    ...row,
    kalkulation_json: writeGdMeta(null, nextMeta),
  };
}

/** Lieferschein: Beauftragt/Geliefert/Rückstand setzen und `menge` = geliefert halten. */
export function applyLieferscheinDelivery(
  row: EditorPosition,
  update: {
    beauftragt?: number;
    geliefert?: number;
    rueckstandEntfernt?: boolean;
  },
): EditorPosition {
  const current = readLieferscheinDelivery(row);
  const meta = getPositionMeta(row);
  let beauftragt =
    update.beauftragt != null && Number.isFinite(update.beauftragt)
      ? Math.max(0, Number(update.beauftragt))
      : current.beauftragte_menge;
  let geliefert =
    update.geliefert != null && Number.isFinite(update.geliefert)
      ? Math.max(0, Number(update.geliefert))
      : current.geliefert_menge;
  let entfernt =
    update.rueckstandEntfernt != null
      ? Boolean(update.rueckstandEntfernt)
      : meta.rueckstand_entfernt === true;

  if (update.geliefert != null) {
    entfernt = false;
  }
  if (update.beauftragt != null && geliefert > beauftragt && !entfernt) {
    geliefert = beauftragt;
  }
  if (update.rueckstandEntfernt === true) {
    entfernt = true;
  }

  const rueckstand = entfernt ? 0 : Math.max(0, beauftragt - geliefert);
  return {
    ...patchPositionMeta(row, {
      beauftragte_menge: beauftragt,
      geliefert_menge: geliefert,
      rueckstand_menge: rueckstand,
      rueckstand_entfernt: entfernt,
    }),
    menge: geliefert,
  };
}

function defaultKalkulation(row: DokumentPosition): LeistungKalkulation | null {
  if (normalizePositionTyp(row.position_typ) !== "leistung") return null;
  return {
    artikel_liste: [],
    lohn_liste: [],
    stunden: 0,
    stundensatz_netto: 0,
    pauschale_netto: 0,
    aufschlag_prozent: 0,
  };
}

export type NachweisStundenArtikelDefaults = {
  artikel_id?: number | null;
  hero_nr?: string | null;
  bezeichnung?: string | null;
  einheit?: string | null;
  einzelpreis_netto?: number | null;
  rabatt_prozent?: number | null;
};

/** Stellt sicher, dass die Position einen stabilen media_key für Bilder hat. */
export function ensurePositionMediaKey(row: EditorPosition): EditorPosition {
  const existing = (getPositionMeta(row).media_key || "").trim();
  if (existing) return row;
  return patchPositionMeta(row, { media_key: newLocalId() });
}

export function createBlockPosition(
  typ: PaletteBlockTyp,
  sortIndex: number,
  options?: {
    lohnarten?: GdLohnart[];
    stundenArtikel?: NachweisStundenArtikelDefaults | null;
  },
): EditorPosition {
  if (typ === "nachweisarbeiten") {
    return ensurePositionMediaKey(createNachweisarbeitenPosition(sortIndex, options?.stundenArtikel));
  }

  const base: EditorPosition = {
    localId: newLocalId(),
    sort_index: sortIndex,
    position_typ: typ,
    bezeichnung: "",
    menge: 1,
    einzelpreis_netto: 0,
    rabatt_prozent: 0,
    steuersatz_prozent: 19,
  };
  if (typ === "leistung") {
    const lohnarten = options?.lohnarten ?? [];
    base.kalkulation_json = {
      artikel_liste: [],
      lohn_liste: lohnarten.map((art) => newLeistungLohnFromArt(art)),
      stunden: 0,
      stundensatz_netto: 0,
      pauschale_netto: 0,
      aufschlag_prozent: 0,
    };
    return ensurePositionMediaKey(patchPositionMeta(base, { preisart: "festpreis" }));
  }
  if (typ === "artikel") {
    return ensurePositionMediaKey(patchPositionMeta(base, { preisart: "festpreis" }));
  }
  if (typ === "titel") {
    base.bezeichnung = "Neuer Titel";
  }
  if (typ === "text") {
    base.bezeichnung = "";
    base.beschreibung = "";
  }
  return base;
}

export function createNachweisarbeitenPosition(
  sortIndex: number,
  stundenArtikel?: NachweisStundenArtikelDefaults | null,
): EditorPosition {
  const artikelId = stundenArtikel?.artikel_id ?? null;
  const bezeichnung = (stundenArtikel?.bezeichnung || "").trim();
  const heroNr = (stundenArtikel?.hero_nr || "").trim() || null;
  const einheit = (stundenArtikel?.einheit || "").trim() || "Std.";
  const einzelpreis =
    stundenArtikel?.einzelpreis_netto != null && Number.isFinite(Number(stundenArtikel.einzelpreis_netto))
      ? Number(stundenArtikel.einzelpreis_netto)
      : 0;
  const rabatt =
    stundenArtikel?.rabatt_prozent != null && Number.isFinite(Number(stundenArtikel.rabatt_prozent))
      ? Number(stundenArtikel.rabatt_prozent)
      : 0;
  const base: EditorPosition = {
    localId: newLocalId(),
    sort_index: sortIndex,
    position_typ: "artikel",
    bezeichnung,
    einheit,
    menge: 1,
    einzelpreis_netto: einzelpreis,
    rabatt_prozent: rabatt,
    steuersatz_prozent: 19,
    artikel_id: artikelId,
    hero_nr: heroNr,
    ist_freier_artikel: artikelId == null,
  };
  return patchPositionMeta(base, {
    preisart: "nachweis",
    nachweis_hinweis: DEFAULT_NACHWEIS_HINWEIS,
  });
}

/** Nachweisposition mit gebuchten Projektstunden als bereits angefallene Kosten. */
export function createGebuchteStundenPosition(
  sortIndex: number,
  totalHours: number,
  stundenArtikel?: NachweisStundenArtikelDefaults | null,
): EditorPosition {
  const hours = Number.isFinite(totalHours) && totalHours > 0 ? Math.round(totalHours * 100) / 100 : 0;
  const base = createNachweisarbeitenPosition(sortIndex, stundenArtikel);
  const artikelName = (stundenArtikel?.bezeichnung || "").trim();
  return {
    ...patchPositionMeta(base, {
      preisart: "nachweis",
      nachweis_hinweis: DEFAULT_GEBUCHTE_STUNDEN_HINWEIS,
    }),
    bezeichnung: artikelName || "Bereits geleistete Stunden",
    menge: hours > 0 ? hours : 1,
    einheit: (stundenArtikel?.einheit || "").trim() || "Std.",
  };
}

/** Wendet den Kunden-Stundenartikel auf eine leere Nachweisposition an. */
export function applyStundenArtikelToNachweisPosition(
  row: EditorPosition,
  stundenArtikel: NachweisStundenArtikelDefaults | null | undefined,
): EditorPosition {
  if (!stundenArtikel) return toNachweisArtikelPosition(row);
  const artikelId = stundenArtikel.artikel_id ?? null;
  if (artikelId == null && !(stundenArtikel.hero_nr || "").trim()) {
    return toNachweisArtikelPosition(row);
  }
  // Bereits befüllte Positionen nicht überschreiben
  if (row.artikel_id != null || (row.bezeichnung || "").trim()) {
    return toNachweisArtikelPosition(row);
  }
  return toNachweisArtikelPosition({
    ...row,
    artikel_id: artikelId,
    hero_nr: (stundenArtikel.hero_nr || "").trim() || null,
    bezeichnung: (stundenArtikel.bezeichnung || "").trim(),
    einheit: (stundenArtikel.einheit || "").trim() || "Std.",
    einzelpreis_netto:
      stundenArtikel.einzelpreis_netto != null && Number.isFinite(Number(stundenArtikel.einzelpreis_netto))
        ? Number(stundenArtikel.einzelpreis_netto)
        : row.einzelpreis_netto,
    rabatt_prozent:
      stundenArtikel.rabatt_prozent != null && Number.isFinite(Number(stundenArtikel.rabatt_prozent))
        ? Number(stundenArtikel.rabatt_prozent)
        : row.rabatt_prozent,
    ist_freier_artikel: artikelId == null,
  });
}

/**
 * Nachweisposition ohne versteckte Kalkulation: Artikel + Meta, kein EK/Aufschlag/Lohn.
 * Bestehende Leistungs-Nachweise werden auf diese Form normalisiert.
 */
export function toNachweisArtikelPosition(row: EditorPosition): EditorPosition {
  const meta = getPositionMeta(row);
  const synced =
    normalizePositionTyp(row.position_typ) === "leistung" ? syncLeistungEinzelpreis(row) : row;
  const nextMeta: GdPositionMeta = {
    interne_notiz: meta.interne_notiz,
    struktur_titel_local_id: meta.struktur_titel_local_id,
    struktur_titel_position_id: meta.struktur_titel_position_id,
    struktur_titel_sort_index: meta.struktur_titel_sort_index,
    preisart: "nachweis",
    nachweis_hinweis: meta.nachweis_hinweis || DEFAULT_NACHWEIS_HINWEIS,
  };
  return {
    ...synced,
    position_typ: "artikel",
    einheit: (synced.einheit || "").trim() || "Std.",
    ist_freier_artikel: synced.artikel_id == null,
    kalkulation_json: writeGdMeta(null, nextMeta) as ArtikelNachkalkulationJson | null,
  };
}

export function isNachweisPosition(row: EditorPosition): boolean {
  return getPositionMeta(row).preisart === "nachweis";
}

export function resolvePositionPreisart(row: EditorPosition): GdPreisart | null {
  const typ = normalizePositionTyp(row.position_typ);
  if (typ !== "artikel" && typ !== "leistung") return null;
  return getPositionMeta(row).preisart === "nachweis" ? "nachweis" : "festpreis";
}

export function positionPreisartBadgeLabel(row: EditorPosition): string | null {
  const preisart = resolvePositionPreisart(row);
  if (preisart === "nachweis") return "Nachweis";
  if (preisart === "festpreis") return "Festpreis";
  return null;
}

export function resolvePositionsart(row: EditorPosition | DokumentPosition): GdPositionsart | null {
  const art = readGdMeta(row.kalkulation_json).positionsart;
  return art === "optional" || art === "alternativ" ? art : null;
}

export function isOptionalOrAlternativPosition(row: EditorPosition | DokumentPosition): boolean {
  return resolvePositionsart(row) != null;
}

export function positionPositionsartBadgeLabel(row: EditorPosition | DokumentPosition): string | null {
  const art = resolvePositionsart(row);
  if (art === "optional") return "Optional · in Summe";
  if (art === "alternativ") return "Alternativ · nicht in Summe";
  return null;
}

/** Markierung entfernen – z. B. bei Übernahme in die Auftragsbestätigung. */
export function stripPositionsart(row: EditorPosition): EditorPosition {
  if (!resolvePositionsart(row)) return row;
  return patchPositionMeta(row, { positionsart: undefined });
}

export function getNachweisHinweis(row: EditorPosition): string {
  if (!isNachweisPosition(row)) return "";
  const raw = (getPositionMeta(row).nachweis_hinweis || "").trim();
  return raw || DEFAULT_NACHWEIS_HINWEIS;
}

export function positionBlockLabel(row: EditorPosition): string {
  if (isNachweisPosition(row)) return POSITION_BLOCK_META.nachweisarbeiten.label;
  return POSITION_BLOCK_META[normalizePositionTyp(row.position_typ)].label;
}

export interface HistorischePositionQuelle {
  quelle?: "app" | "hero" | null;
  position_id: number;
  position_typ: string;
  artikel_id?: number | null;
  bezeichnung: string;
  beschreibung?: string | null;
  hero_nr?: string | null;
  einheit?: string | null;
  menge: number;
  einzelpreis_netto: number;
  rabatt_prozent: number;
  rabatt_betrag?: number;
  rabatt_modus?: "prozent" | "betrag";
  rabatt_ausweis?: "intern" | "dokument";
  steuersatz_prozent: number;
  kalkulation_json?: unknown;
}

/** Übernimmt eine frühere Dokumentposition als neue Editor-Position. */
export function createPositionFromHistorie(
  item: HistorischePositionQuelle,
  sortIndex: number,
): EditorPosition {
  const typ = normalizePositionTyp(item.position_typ);
  const baseTyp: BlockPositionTyp = typ === "leistung" ? "leistung" : "artikel";
  let row = createBlockPosition(baseTyp, sortIndex);
  const sourceMeta = readGdMeta(item.kalkulation_json);
  const { media_key: _ignored, struktur_titel_local_id: _t1, struktur_titel_position_id: _t2, struktur_titel_sort_index: _t3, ...restMeta } =
    sourceMeta;
  row = {
    ...row,
    position_typ: baseTyp,
    artikel_id: item.artikel_id ?? null,
    ist_freier_artikel: item.artikel_id == null,
    bezeichnung: item.bezeichnung || "",
    beschreibung: item.beschreibung ?? null,
    hero_nr: item.hero_nr ?? null,
    einheit: item.einheit ?? row.einheit,
    menge: Number(item.menge) || 1,
    einzelpreis_netto: Number(item.einzelpreis_netto) || 0,
    rabatt_prozent: Number(item.rabatt_prozent) || 0,
    rabatt_betrag: Number(item.rabatt_betrag) || 0,
    rabatt_modus: item.rabatt_modus === "betrag" ? "betrag" : "prozent",
    rabatt_ausweis: item.rabatt_ausweis === "dokument" ? "dokument" : "intern",
    steuersatz_prozent: Number(item.steuersatz_prozent) || 19,
    quelle_position_id: item.quelle === "hero" ? null : item.position_id,
    kalkulation_json: item.kalkulation_json as EditorPosition["kalkulation_json"],
  };
  row = patchPositionMeta(row, {
    ...restMeta,
    media_key: newLocalId(),
    struktur_titel_local_id: null,
    struktur_titel_position_id: null,
    struktur_titel_sort_index: null,
  });
  return ensurePositionMediaKey(row);
}

export function createArtikelPositionFromEinkauf(
  item: EinkaufArtikelPaletteItem,
  sortIndex: number,
  aufschlagProzent: number,
): EditorPosition {
  const ek = Number(item.einzelpreis_netto ?? 0);
  const vk = vkFromEk(ek, aufschlagProzent);
  let row = createBlockPosition("artikel", sortIndex);
  const hasStamm = item.stamm_artikel_id != null && Number(item.stamm_artikel_id) > 0;
  row = {
    ...row,
    artikel_id: hasStamm ? item.stamm_artikel_id : null,
    ist_freier_artikel: !hasStamm,
    bezeichnung: item.bezeichnung || item.stamm_artikel_bezeichnung || "",
    hero_nr: item.stamm_hero_nr ?? item.seller_article_nr ?? null,
    einheit: item.einheit ?? null,
    menge: item.menge != null && Number(item.menge) > 0 ? Number(item.menge) : 1,
    einzelpreis_netto: vk,
    rabatt_prozent: 0,
  };
  return patchArtikelNachkalk(row, {
    einkaufspreis_netto: ek,
    aufschlag_prozent: aufschlagProzent,
    preis_driver: "aufschlag",
  });
}

export function applyArtikelNachkalkPricing(
  row: EditorPosition,
  pricing: {
    einkaufspreis_netto?: number;
    einzelpreis_netto?: number;
    aufschlag_prozent?: number;
    aufschlag_betrag?: number;
    aufschlag_modus?: AufschlagModus;
    preis_driver?: PricingDriver;
  },
): EditorPosition {
  let next = patchArtikelNachkalk(row, {
    einkaufspreis_netto: pricing.einkaufspreis_netto,
    aufschlag_prozent: pricing.aufschlag_prozent,
    ...(pricing.aufschlag_betrag !== undefined ? { aufschlag_betrag: pricing.aufschlag_betrag } : {}),
    ...(pricing.aufschlag_modus !== undefined ? { aufschlag_modus: pricing.aufschlag_modus } : {}),
    preis_driver: pricing.preis_driver,
  });
  if (pricing.einzelpreis_netto != null) {
    next = { ...next, einzelpreis_netto: pricing.einzelpreis_netto };
  }
  return next;
}

export function applyArtikelInterneNotizFromQuelle(
  row: EditorPosition,
  interneBeschreibung: string | undefined,
): EditorPosition {
  if (interneBeschreibung === undefined) return row;
  const trimmed = interneBeschreibung.trim();
  return patchPositionMeta(row, { interne_notiz: trimmed || undefined });
}

export function applyArtikelPricingFromDatabase(
  row: EditorPosition,
  einkaufspreis: number,
  verkaufspreis: number,
  options?: { abgelaufen?: boolean },
): EditorPosition {
  if (options?.abgelaufen) {
    // EP = 0, editierbar (kein Lock über preis_driver=vk); alter Preis nur UI-Hinweis.
    return applyArtikelNachkalkPricing(row, {
      einkaufspreis_netto: Number(einkaufspreis) || 0,
      einzelpreis_netto: 0,
      aufschlag_prozent: 0,
      preis_driver: "aufschlag",
    });
  }
  const pricing = pricingFromDatabase(einkaufspreis, verkaufspreis);
  return applyArtikelNachkalkPricing(row, pricing);
}

function artikelPricingState(row: EditorPosition): ArticlePricingFields {
  const nachkalk = getArtikelNachkalk(row);
  return {
    einkaufspreis_netto: nachkalk.einkaufspreis_netto,
    einzelpreis_netto: row.einzelpreis_netto,
    aufschlag_prozent: nachkalk.aufschlag_prozent,
    aufschlag_betrag: nachkalk.aufschlag_betrag,
    aufschlag_modus: nachkalk.aufschlag_modus,
    preis_driver: nachkalk.preis_driver,
  };
}

export function patchArtikelPricingByEk(row: EditorPosition, einkaufspreis: number): EditorPosition {
  return applyArtikelNachkalkPricing(row, syncPricingField(artikelPricingState(row), "ek", einkaufspreis));
}

export function patchArtikelPricingByVk(row: EditorPosition, verkaufspreis: number): EditorPosition {
  return applyArtikelNachkalkPricing(row, syncPricingField(artikelPricingState(row), "vk", verkaufspreis));
}

export function patchArtikelPricingByAufschlag(row: EditorPosition, aufschlag: number): EditorPosition {
  return applyArtikelNachkalkPricing(
    row,
    syncPricingField(artikelPricingState(row), "aufschlag", aufschlag),
  );
}

export function patchArtikelPricingByAufschlagModus(
  row: EditorPosition,
  modus: AufschlagModus,
): EditorPosition {
  return applyArtikelNachkalkPricing(row, syncPricingAufschlagModus(artikelPricingState(row), modus));
}

export function syncLeistungArtikelPricing(
  item: LeistungArtikelZeile,
  field: "ek" | "vk" | "aufschlag",
  value: number,
): LeistungArtikelZeile {
  const pricing = syncPricingField(
    {
      einkaufspreis_netto: item.einkaufspreis_netto,
      einzelpreis_netto: item.einzelpreis_netto,
      aufschlag_prozent: item.aufschlag_prozent,
      preis_driver: item.preis_driver,
      aufschlag_modus: "prozent",
    },
    field,
    value,
  );
  return {
    ...item,
    einkaufspreis_netto: pricing.einkaufspreis_netto,
    einzelpreis_netto: pricing.einzelpreis_netto ?? item.einzelpreis_netto,
    aufschlag_prozent: pricing.aufschlag_prozent,
    preis_driver: pricing.preis_driver,
  };
}

export function applyLeistungArtikelPricingFromDatabase(
  item: LeistungArtikelZeile,
  einkaufspreis: number,
  verkaufspreis: number,
  aufschlag?: number,
  options?: { abgelaufen?: boolean },
): LeistungArtikelZeile {
  if (options?.abgelaufen) {
    return {
      ...item,
      einkaufspreis_netto: Number(einkaufspreis) || 0,
      einzelpreis_netto: 0,
      aufschlag_prozent: 0,
      preis_driver: "aufschlag",
    };
  }
  const pricing = pricingFromDatabase(einkaufspreis, verkaufspreis);
  return {
    ...item,
    einkaufspreis_netto: pricing.einkaufspreis_netto,
    einzelpreis_netto: pricing.einzelpreis_netto,
    aufschlag_prozent: aufschlag ?? pricing.aufschlag_prozent,
    preis_driver: pricing.preis_driver,
  };
}

export function getArtikelNachkalk(row: EditorPosition): ArtikelNachkalkulationJson {
  if (normalizePositionTyp(row.position_typ) !== "artikel") return {};
  const raw = (row.kalkulation_json as ArtikelNachkalkulationJson | null | undefined) || {};
  const { _gd_meta: _ignored, ...nachkalk } = raw;
  return nachkalk;
}

export function patchArtikelNachkalk(
  row: EditorPosition,
  partial: Partial<ArtikelNachkalkulationJson>,
): EditorPosition {
  const meta = getPositionMeta(row);
  const current = getArtikelNachkalk(row);
  const next = { ...current, ...partial };
  const hasNachkalk =
    (next.einkaufspreis_netto != null && next.einkaufspreis_netto > 0) ||
    (next.aufschlag_prozent != null && next.aufschlag_prozent !== 0) ||
    (next.aufschlag_betrag != null && next.aufschlag_betrag !== 0) ||
    next.aufschlag_modus === "betrag" ||
    next.preis_driver != null;
  const payload = hasNachkalk ? next : null;
  return {
    ...row,
    kalkulation_json: writeGdMeta(payload, meta) as ArtikelNachkalkulationJson | null,
  };
}

export function positionDisplayTitle(row: EditorPosition): string {
  const title = (row.bezeichnung || "").trim();
  const typ = normalizePositionTyp(row.position_typ);
  if (title) return title;
  if (typ === "titel") return "Neuer Titel";
  if (typ === "text") return "Textblock";
  return "Ohne Bezeichnung";
}

export function positionDisplayDescription(row: EditorPosition): string {
  const text = (row.beschreibung || "").trim();
  if (text) return text;
  const typ = normalizePositionTyp(row.position_typ);
  // Textblöcke dürfen nur aus dem optionalen Titel bestehen — kein
  // Platzhalter wie „Kein Text“, der ins Dokument/PDF rutschen würde.
  if (typ === "text" || typ === "titel") return "";
  return "Keine Beschreibung";
}

export function positionSummary(row: EditorPosition): string {
  const typ = normalizePositionTyp(row.position_typ);
  const label = positionBlockLabel(row);
  const title = (row.bezeichnung || "").trim();
  if (typ === "titel" || typ === "text") {
    return title || label;
  }
  if (typ === "leistung") {
    const kalk = row.kalkulation_json as LeistungKalkulation | null | undefined;
    const count = kalk?.artikel_liste?.length ?? 0;
    return title ? `${title} · ${count} Artikel` : `${label} · ${count} Artikel`;
  }
  if (isFreierArtikel(row)) {
    return title ? `${title} · freier Artikel` : "Freier Artikel";
  }
  return title || row.hero_nr || label;
}

export function positionTotals(row: EditorPosition): PositionTotals {
  return calcRowForPosition(row);
}

export function isFreierArtikel(row: EditorPosition): boolean {
  if (normalizePositionTyp(row.position_typ) !== "artikel") return false;
  if (row.ist_freier_artikel === true) return true;
  if (row.ist_freier_artikel === false) return false;
  return row.artikel_id == null;
}

/**
 * Wechsel Artikeldatenbank → Freier Artikel:
 * nur Artikelnummer/ID entfernen, alle übrigen Positionsdaten behalten (inkl. Menge).
 */
export function switchArtikelToFreierArtikel(row: EditorPosition): EditorPosition {
  if (normalizePositionTyp(row.position_typ) !== "artikel") return row;
  return {
    ...row,
    ist_freier_artikel: true,
    artikel_id: null,
    hero_nr: null,
  };
}

function visibleArtikelText(value: string | null | undefined): string {
  return String(value || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
}

function hasNonZeroAmount(value: number | null | undefined): boolean {
  const amount = Number(value);
  return Number.isFinite(amount) && amount !== 0;
}

/**
 * Wechsel Freier Artikel → Artikeldatenbank verwirft Bezeichnung, Beschreibung,
 * Preise, Einheit, Artikelnummer und die Nachkalkulation. Menge, Rabatt, MwSt
 * und die interne Notiz bleiben erhalten und zählen hier nicht.
 */
export function hasArtikeldatenToClearForStammSwitch(row: EditorPosition): boolean {
  if (normalizePositionTyp(row.position_typ) !== "artikel") return false;
  if (visibleArtikelText(row.bezeichnung)) return true;
  if (visibleArtikelText(row.beschreibung)) return true;
  if ((row.einheit || "").trim()) return true;
  if ((row.hero_nr || "").trim()) return true;
  if (row.artikel_id != null) return true;
  if (hasNonZeroAmount(row.einzelpreis_netto)) return true;
  const nachkalk = getArtikelNachkalk(row);
  if (hasNonZeroAmount(nachkalk.einkaufspreis_netto)) return true;
  if (hasNonZeroAmount(nachkalk.aufschlag_prozent)) return true;
  if (hasNonZeroAmount(nachkalk.aufschlag_betrag)) return true;
  return false;
}

/**
 * Wechsel Freier Artikel → Artikeldatenbank nach Bestätigung:
 * Artikelinformationen leeren, Menge (und Rabatt/MwSt) behalten.
 */
export function clearArtikeldatenForStammSwitch(row: EditorPosition): EditorPosition {
  if (normalizePositionTyp(row.position_typ) !== "artikel") return row;
  const meta = getPositionMeta(row);
  return {
    ...row,
    ist_freier_artikel: false,
    artikel_id: null,
    hero_nr: null,
    bezeichnung: "",
    beschreibung: null,
    einheit: null,
    einzelpreis_netto: 0,
    // Menge / Rabatt / MwSt bewusst behalten
    kalkulation_json: writeGdMeta(null, meta) as EditorPosition["kalkulation_json"],
  };
}

/** Dupliziert eine Position inkl. Kalkulation; neue IDs, ohne DB-/Anhang-Verknüpfung. */
export function duplicateEditorPosition(row: EditorPosition, sortIndex: number): EditorPosition {
  const cloned = structuredClone(row) as EditorPosition;
  let next: EditorPosition = {
    ...cloned,
    id: undefined,
    dokument_id: undefined,
    position_nr: undefined,
    quelle_position_id: undefined,
    zeilen_netto: undefined,
    zeilen_steuer: undefined,
    zeilen_brutto: undefined,
    localId: newLocalId("dup"),
    sort_index: sortIndex,
  };

  const typ = normalizePositionTyp(next.position_typ);
  if (typ === "leistung") {
    const kalk = (next.kalkulation_json || {
      artikel_liste: [],
      lohn_liste: [],
    }) as LeistungKalkulation;
    next = {
      ...next,
      kalkulation_json: {
        ...kalk,
        artikel_liste: (kalk.artikel_liste || []).map((item) => ({
          ...item,
          localId: newLocalId("art"),
        })),
        lohn_liste: (kalk.lohn_liste || []).map((item) => ({
          ...item,
          localId: newLocalId("lohn"),
        })),
      },
    };
  }

  const meta = getPositionMeta(next);
  const {
    media_key: _mk,
    merged_children: mergedChildren,
    ...restMeta
  } = meta;
  next = patchPositionMeta(next, {
    ...restMeta,
    media_key: newLocalId(),
    merged_children: Array.isArray(mergedChildren)
      ? mergedChildren.map((child) => structuredClone(child))
      : mergedChildren,
  });
  return ensurePositionMediaKey(next);
}

export function duplicatePositionInList(
  positions: EditorPosition[],
  localId: string,
): EditorPosition[] {
  const index = positions.findIndex((row) => row.localId === localId);
  if (index < 0) return positions;
  const source = positions[index];
  if (!source) return positions;
  const insertAt = index + 1;
  const copy = duplicateEditorPosition(source, insertAt);
  const next = [...positions.slice(0, insertAt), copy, ...positions.slice(insertAt)];
  return next.map((row, idx) => ({ ...row, sort_index: idx }));
}

export function isStammArtikel(row: EditorPosition): boolean {
  return normalizePositionTyp(row.position_typ) === "artikel" && !isFreierArtikel(row);
}

/** EP stammt direkt aus der Artikeldatenbank (Hero-VK), nicht manuell editierbar. */
export function isArtikelEpFromDatabase(row: EditorPosition): boolean {
  if (!isStammArtikel(row) || row.artikel_id == null) return false;
  return getArtikelNachkalk(row).preis_driver === "vk";
}

export function isLeistungArtikelEpFromDatabase(item: LeistungArtikelZeile): boolean {
  if (item.ist_freier_artikel || item.artikel_id == null) return false;
  return item.preis_driver === "vk";
}

export function countPositionsInTitelGroup(positions: EditorPosition[], titelLocalId: string): number {
  return positions.filter((row) => {
    if (normalizePositionTyp(row.position_typ) === "titel") return false;
    return getPositionMeta(row).struktur_titel_local_id === titelLocalId;
  }).length;
}

export function artikelPositionToLeistungZeile(row: EditorPosition): LeistungArtikelZeile {
  const nachkalk = getArtikelNachkalk(row);
  const meta = getPositionMeta(row);
  return {
    localId: newLocalId("art"),
    artikel_id: isFreierArtikel(row) ? null : row.artikel_id,
    ist_freier_artikel: isFreierArtikel(row),
    bezeichnung: row.bezeichnung,
    hero_nr: row.hero_nr,
    einheit: row.einheit,
    menge: row.menge,
    einzelpreis_netto: row.einzelpreis_netto,
    einkaufspreis_netto: nachkalk.einkaufspreis_netto,
    aufschlag_prozent: nachkalk.aufschlag_prozent,
    rabatt_prozent: row.rabatt_prozent,
    interne_beschreibung: meta.interne_notiz || undefined,
  };
}

export function absorbArtikelIntoLeistung(
  positions: EditorPosition[],
  artikelIndex: number,
  leistungIndex: number,
): EditorPosition[] {
  if (artikelIndex === leistungIndex) return positions;
  const artikel = positions[artikelIndex];
  const leistung = positions[leistungIndex];
  if (!artikel || normalizePositionTyp(artikel.position_typ) !== "artikel") return positions;
  if (!leistung || normalizePositionTyp(leistung.position_typ) !== "leistung") return positions;

  const kalk = (leistung.kalkulation_json || { artikel_liste: [], lohn_liste: [] }) as LeistungKalkulation;
  const updatedLeistung = syncLeistungEinzelpreis({
    ...leistung,
    kalkulation_json: {
      ...kalk,
      artikel_liste: [...(kalk.artikel_liste || []), artikelPositionToLeistungZeile(artikel)],
    },
  });

  return positions
    .filter((_, index) => index !== artikelIndex)
    .map((row, idx) =>
      row.localId === updatedLeistung.localId ? { ...updatedLeistung, sort_index: idx } : { ...row, sort_index: idx },
    );
}

export function assignPositionToTitel(
  positions: EditorPosition[],
  positionIndex: number,
  titelLocalId: string | null,
): EditorPosition[] {
  const titelRef = resolveTitelReference(titelLocalId, positions);
  return positions.map((row, index) =>
    index === positionIndex ? patchPositionMeta(row, titelRef) : row,
  );
}

/** Einfüge-Index direkt hinter dem letzten Element einer Titelgruppe (oder hinter dem Titel). */
export function findTitelGroupEndInsertIndex(
  positions: EditorPosition[],
  titelLocalId: string,
): number {
  const titelIndex = positions.findIndex(
    (row) => row.localId === titelLocalId && normalizePositionTyp(row.position_typ) === "titel",
  );
  if (titelIndex < 0) return positions.length;
  let end = titelIndex + 1;
  while (end < positions.length) {
    const row = positions[end];
    if (!row || getPositionMeta(row).struktur_titel_local_id !== titelLocalId) break;
    end += 1;
  }
  return end;
}

export function calcLeistungMaterialNetto(
  kalkulation: LeistungKalkulation | null | undefined,
  positionsMenge: unknown = 1,
): number {
  if (!kalkulation?.artikel_liste?.length) return 0;
  return kalkulation.artikel_liste.reduce(
    (sum, item) => sum + calcLeistungArtikelZeileAnteilEp(item, positionsMenge),
    0,
  );
}

export function calcLeistungArtikelZeileNetto(item: LeistungArtikelZeile): number {
  return calcPosition(item.menge, item.einzelpreis_netto, item.rabatt_prozent, 0).zeilen_netto;
}

/** Zeilensumme, wie sie in den Einzelpreis der Leistung eingeht. Gesamt wird durch die Menge geteilt. */
export function calcLeistungArtikelZeileAnteilEp(
  item: LeistungArtikelZeile,
  positionsMenge: unknown = 1,
): number {
  return betragAnteilEinzelpreis(calcLeistungArtikelZeileNetto(item), item.preisbezug, positionsMenge);
}

export function calcLeistungEinkaufNetto(
  kalkulation: LeistungKalkulation | null | undefined,
  positionsMenge: unknown = 1,
): number {
  if (!kalkulation?.artikel_liste?.length) return 0;
  return kalkulation.artikel_liste.reduce((sum, item) => {
    const ek = item.einkaufspreis_netto ?? item.einzelpreis_netto;
    const line = calcPosition(item.menge, ek, 0, 0).zeilen_netto;
    return sum + betragAnteilEinzelpreis(line, item.preisbezug, positionsMenge);
  }, 0);
}

export function calcLeistungLohnZeileNetto(item: LeistungLohnZeile): number {
  return Number(item.stunden || 0) * Number(item.stundensatz_netto || 0);
}

/** Lohnzeile, wie sie in den Einzelpreis eingeht. Gesamt wird durch die Menge geteilt. */
export function calcLeistungLohnZeileAnteilEp(
  item: LeistungLohnZeile,
  positionsMenge: unknown = 1,
): number {
  return betragAnteilEinzelpreis(calcLeistungLohnZeileNetto(item), item.preisbezug, positionsMenge);
}

export function calcLeistungLohnNetto(
  kalkulation: LeistungKalkulation | null | undefined,
  positionsMenge: unknown = 1,
): number {
  if (kalkulation?.lohn_liste?.length) {
    return Math.round(
      (kalkulation.lohn_liste.reduce(
        (sum, item) => sum + calcLeistungLohnZeileAnteilEp(item, positionsMenge),
        0,
      ) +
        Number.EPSILON) *
        100,
    ) / 100;
  }
  return Math.round(
    (Number(kalkulation?.stunden || 0) * Number(kalkulation?.stundensatz_netto || 0) + Number.EPSILON) * 100,
  ) / 100;
}

export function calcLeistungBaseNetto(
  kalkulation: LeistungKalkulation | null | undefined,
  positionsMenge: unknown = 1,
): number {
  return calcLeistungKalkuliertNetto(kalkulation, positionsMenge);
}

export function syncLeistungEinzelpreis(row: EditorPosition): EditorPosition {
  if (row.position_typ !== "leistung") return row;
  const rawKalk = (row.kalkulation_json || {
    artikel_liste: [],
    lohn_liste: [],
  }) as LeistungKalkulation;
  const kalk = syncLeistungVkDriverOnKalkulationChange(rawKalk);
  const vk = resolveLeistungVkNetto(kalk, row.einzelpreis_netto, row.menge);
  return {
    ...row,
    kalkulation_json: kalk,
    einzelpreis_netto: vk,
  };
}

export function toApiPosition(
  row: EditorPosition,
  index: number,
  all: EditorPosition[],
): DokumentPosition {
  const synced = row.position_typ === "leistung" ? syncLeistungEinzelpreis(row) : row;
  const typ = normalizePositionTyp(synced.position_typ);
  const meta = getPositionMeta(synced);
  const titelRef = resolveTitelReference(meta.struktur_titel_local_id, all);
  const resolvedMeta: GdPositionMeta = {
    ...meta,
    ...titelRef,
    struktur_titel_local_id: undefined,
  };

  let kalkulation_json: unknown = null;
  if (typ === "leistung") {
    kalkulation_json = writeGdMeta(synced.kalkulation_json, resolvedMeta);
  } else if (typ === "artikel") {
    const nachkalk = getArtikelNachkalk(synced);
    kalkulation_json = writeGdMeta(
      Object.keys(nachkalk).length ? nachkalk : null,
      resolvedMeta,
    );
  } else {
    kalkulation_json = writeGdMeta(null, resolvedMeta);
  }

  return {
    id: synced.id,
    position_nr: index + 1,
    sort_index: index,
    position_typ: synced.position_typ,
    artikel_id: synced.position_typ === "artikel" && isStammArtikel(synced) ? synced.artikel_id : null,
    ist_freier_artikel: synced.position_typ === "artikel" ? isFreierArtikel(synced) : undefined,
    bezeichnung: synced.bezeichnung || "",
    beschreibung: synced.beschreibung,
    hero_nr: isFreierArtikel(synced) ? synced.hero_nr : synced.hero_nr,
    einheit: synced.einheit,
    menge: synced.menge,
    einzelpreis_netto: synced.einzelpreis_netto,
    rabatt_prozent: Number(synced.rabatt_prozent) || 0,
    rabatt_betrag: Number(synced.rabatt_betrag) || 0,
    rabatt_modus: synced.rabatt_modus === "betrag" ? "betrag" : "prozent",
    rabatt_ausweis: synced.rabatt_ausweis === "dokument" ? "dokument" : "intern",
    steuersatz_prozent: synced.steuersatz_prozent,
    quelle_position_id: synced.quelle_position_id,
    kalkulation_json: kalkulation_json as DokumentPosition["kalkulation_json"],
  };
}

export function reorderPositions(items: EditorPosition[], from: number, to: number): EditorPosition[] {
  if (from === to || from < 0 || to < 0 || from >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (!moved) return items;
  next.splice(to, 0, moved);
  return next.map((row, idx) => ({ ...row, sort_index: idx }));
}

/** Fügt die gezogene Position an `insertAt` ein (0 = vor erster Position). */
export function reorderToInsertIndex(
  items: EditorPosition[],
  from: number,
  insertAt: number,
): EditorPosition[] {
  if (from < 0 || from >= items.length) return items;
  const clamped = Math.max(0, Math.min(insertAt, items.length));
  let target = clamped;
  if (from < clamped) target = clamped - 1;
  if (from === target) return items;
  return reorderPositions(items, from, target);
}

function finalInsertIndex(from: number, insertAt: number, length: number): number {
  const clamped = Math.max(0, Math.min(insertAt, length));
  if (from < 0 || from >= length) return clamped;
  if (from < clamped) return clamped - 1;
  if (from === clamped) return from;
  return clamped;
}

export interface InsertPointer {
  clientX: number;
  clientY: number;
  rect: DOMRect;
}

/** Titel-Zuordnung für Einfügen per Drop-Slot: string = zuordnen, null = explizit lösen, undefined = unverändert. */
export function resolveInsertTitelAssignment(
  positions: EditorPosition[],
  insertAt: number,
  pointer?: InsertPointer,
): string | null | undefined {
  const prev = insertAt > 0 ? positions[insertAt - 1] : undefined;
  const next = insertAt < positions.length ? positions[insertAt] : undefined;
  const prevTitel = prev ? getPositionMeta(prev).struktur_titel_local_id ?? null : null;
  const nextTitel = next ? getPositionMeta(next).struktur_titel_local_id ?? null : null;
  const prevTyp = prev ? normalizePositionTyp(prev.position_typ) : null;

  if (prevTitel && nextTitel && prevTitel === nextTitel) {
    return prevTitel;
  }

  // Direkt hinter einem Titel-Header → zur Gruppe gehören (auch bei leerer Gruppe).
  if (prevTyp === "titel" && prev) {
    return prev.localId;
  }

  if (nextTitel && prevTitel !== nextTitel && !prevTitel) {
    if (prevTyp !== "titel") return nextTitel;
  }

  if (prevTitel && prevTitel !== nextTitel) {
    if (!pointer) return prevTitel;
    const relX = (pointer.clientX - pointer.rect.left) / Math.max(pointer.rect.width, 1);
    const relY = (pointer.clientY - pointer.rect.top) / Math.max(pointer.rect.height, 1);
    return relX - relY > 0 ? prevTitel : null;
  }

  return undefined;
}

export function isTitelGroupBoundarySlot(positions: EditorPosition[], insertAt: number): boolean {
  const prev = insertAt > 0 ? positions[insertAt - 1] : undefined;
  const next = insertAt < positions.length ? positions[insertAt] : undefined;
  if (!prev) return false;
  const prevTitel = getPositionMeta(prev).struktur_titel_local_id ?? null;
  const nextTitel = next ? getPositionMeta(next).struktur_titel_local_id ?? null : null;
  return Boolean(prevTitel && prevTitel !== nextTitel);
}

export function applyReorderWithTitelAssignment(
  positions: EditorPosition[],
  from: number,
  insertAt: number,
  titelAssignment: string | null | undefined,
): EditorPosition[] {
  const reordered = reorderToInsertIndex(positions, from, insertAt);
  if (titelAssignment === undefined) return reordered;
  const newIndex = finalInsertIndex(from, insertAt, positions.length);
  const moved = reordered[newIndex];
  if (!moved || normalizePositionTyp(moved.position_typ) === "titel") return reordered;
  return assignPositionToTitel(reordered, newIndex, titelAssignment);
}

/**
 * Position einer Titelgruppe zuordnen und – falls nötig – ans Ende der Gruppe verschieben.
 * Verhindert den Effekt, dass Meta gesetzt wird, die Position aber optisch „zurückspringt“.
 */
export function movePositionIntoTitel(
  positions: EditorPosition[],
  fromIndex: number,
  titelLocalId: string,
): EditorPosition[] {
  const dragged = positions[fromIndex];
  if (!dragged || normalizePositionTyp(dragged.position_typ) === "titel") return positions;
  const titelExists = positions.some(
    (row) => row.localId === titelLocalId && normalizePositionTyp(row.position_typ) === "titel",
  );
  if (!titelExists) return positions;

  const currentTitel = getPositionMeta(dragged).struktur_titel_local_id ?? null;
  if (currentTitel === titelLocalId) {
    return assignPositionToTitel(positions, fromIndex, titelLocalId);
  }

  const insertAt = findTitelGroupEndInsertIndex(positions, titelLocalId);
  return applyReorderWithTitelAssignment(positions, fromIndex, insertAt, titelLocalId);
}

/** Übernehmen im Positionsdialog: neue Bausteine einfügen, bestehende nur aktualisieren. */
export function applyPositionEditorSave(
  positions: EditorPosition[],
  pending: ProvisionalPositionDraft | null,
  saved: EditorPosition,
): EditorPosition[] {
  if (pending && pending.position.localId === saved.localId) {
    return insertPositionWithTitelAssignment(
      positions,
      saved,
      pending.insertAt,
      pending.titelAssignment,
    );
  }
  return positions.map((row) => (row.localId === saved.localId ? saved : row));
}

export function insertPositionWithTitelAssignment(
  positions: EditorPosition[],
  block: EditorPosition,
  insertAt: number,
  titelAssignment: string | null | undefined,
): EditorPosition[] {
  const clamped = Math.max(0, Math.min(insertAt, positions.length));
  const merged = [...positions];
  merged.splice(clamped, 0, block);
  let next = merged.map((row, idx) => ({ ...row, sort_index: idx }));
  if (
    titelAssignment !== undefined &&
    normalizePositionTyp(block.position_typ) !== "titel"
  ) {
    next = assignPositionToTitel(next, clamped, titelAssignment);
  }
  return next;
}


export type CardDropZone = "before" | "after" | "into";

export function getCardDropZone(clientY: number, rect: DOMRect, allowInto: boolean): CardDropZone {
  const ratio = (clientY - rect.top) / Math.max(rect.height, 1);
  if (allowInto) {
    // Schmale Ränder für davor/danach, großer Mittelbereich für „in Titel/Leistung“.
    if (ratio < 0.2) return "before";
    if (ratio > 0.8) return "after";
    return "into";
  }
  return ratio < 0.5 ? "before" : "after";
}

/** Kein sichtbarer Move, wenn die Einfügeposition der aktuellen Stelle entspricht. */
export function isNoOpInsertAt(fromIndex: number, insertAt: number): boolean {
  return insertAt === fromIndex || insertAt === fromIndex + 1;
}

export function reorderFromCardDrop(
  items: EditorPosition[],
  from: number,
  cardIndex: number,
  zone: CardDropZone,
): EditorPosition[] {
  if (zone === "into") return items;
  const insertAt = zone === "before" ? cardIndex : cardIndex + 1;
  return reorderToInsertIndex(items, from, insertAt);
}

/** Übernimmt Bezeichnung, Einheit und Menge der Leistung in eine Artikelzeile. Preise bleiben stehen. */
export function applyLeistungFieldsToArtikelZeile(
  item: LeistungArtikelZeile,
  source: { bezeichnung?: string | null; einheit?: string | null; menge?: number | null },
): LeistungArtikelZeile {
  const menge = Number(source.menge);
  const fromDatabase = !item.ist_freier_artikel && item.artikel_id != null;
  return {
    ...item,
    bezeichnung: String(source.bezeichnung || "").trim(),
    einheit: source.einheit ? String(source.einheit) : null,
    menge: Number.isFinite(menge) ? menge : item.menge,
    ist_freier_artikel: true,
    artikel_id: null,
    hero_nr: null,
    ...(fromDatabase ? { preis_driver: undefined } : {}),
  };
}

export function newLeistungArtikel(options?: { einheit?: string | null }): LeistungArtikelZeile {
  return {
    localId: newLocalId("art"),
    bezeichnung: "",
    menge: 1,
    einheit: options?.einheit ?? null,
    einzelpreis_netto: 0,
    rabatt_prozent: 0,
    ist_freier_artikel: true,
  };
}

export function newLeistungLohnFromArt(art: GdLohnart): LeistungLohnZeile {
  return {
    localId: newLocalId("lohn"),
    lohnart_id: art.id,
    bezeichnung: art.bezeichnung,
    stunden: 0,
    stundensatz_netto: art.stundensatz_netto,
  };
}

export type PositionSelectionMode = "merge" | "merge-extend" | "titel";

export function isMergeablePosition(row: EditorPosition): boolean {
  const typ = normalizePositionTyp(row.position_typ);
  return typ === "artikel" || typ === "leistung";
}

export function isMergedContainerPosition(row: EditorPosition): boolean {
  const children = getPositionMeta(row).merged_children;
  return Array.isArray(children) && children.length > 0;
}

export function snapshotMergedChild(row: EditorPosition): MergedChildSnapshot {
  const meta = getPositionMeta(row);
  return {
    position_typ: row.position_typ,
    bezeichnung: row.bezeichnung || "",
    beschreibung: row.beschreibung,
    artikel_id: row.artikel_id,
    ist_freier_artikel: row.ist_freier_artikel,
    hero_nr: row.hero_nr,
    einheit: row.einheit,
    menge: row.menge,
    einzelpreis_netto: row.einzelpreis_netto,
    rabatt_prozent: row.rabatt_prozent,
    rabatt_betrag: row.rabatt_betrag,
    rabatt_modus: row.rabatt_modus,
    rabatt_ausweis: row.rabatt_ausweis,
    steuersatz_prozent: row.steuersatz_prozent,
    kalkulation_json: row.kalkulation_json,
    interne_notiz: meta.interne_notiz,
  };
}

export function mergedChildToEditorPosition(child: MergedChildSnapshot): EditorPosition {
  return restoreMergedChild(child, 0);
}

function restoreMergedChild(child: MergedChildSnapshot, index: number): EditorPosition {
  const row: EditorPosition = {
    localId: newLocalId("restored"),
    sort_index: index,
    position_typ: normalizePositionTyp(child.position_typ) as PositionTyp,
    bezeichnung: child.bezeichnung || "",
    beschreibung: child.beschreibung,
    artikel_id: child.artikel_id,
    ist_freier_artikel: child.ist_freier_artikel,
    hero_nr: child.hero_nr,
    einheit: child.einheit,
    menge: Number(child.menge ?? 1),
    einzelpreis_netto: Number(child.einzelpreis_netto ?? 0),
    rabatt_prozent: Number(child.rabatt_prozent ?? 0),
    rabatt_betrag: Number(child.rabatt_betrag ?? 0),
    rabatt_modus: child.rabatt_modus === "betrag" ? "betrag" : "prozent",
    rabatt_ausweis: child.rabatt_ausweis === "dokument" ? "dokument" : "intern",
    steuersatz_prozent: child.steuersatz_prozent ?? 19,
    kalkulation_json: child.kalkulation_json as EditorPosition["kalkulation_json"],
  };
  if (child.interne_notiz?.trim()) {
    return patchPositionMeta(row, { interne_notiz: child.interne_notiz.trim() });
  }
  return row;
}

export function mergePositionsIntoLeistung(
  positions: EditorPosition[],
  selectedLocalIds: string[],
): EditorPosition[] {
  const selectedSet = new Set(selectedLocalIds);
  const mergeable = positions.filter(
    (row) => selectedSet.has(row.localId) && isMergeablePosition(row),
  );
  if (mergeable.length < 2) return positions;

  const firstMergeable = mergeable[0];
  if (!firstMergeable) return positions;
  const firstIndex = positions.findIndex((row) => row.localId === firstMergeable.localId);
  if (firstIndex < 0) return positions;

  let artikelListe: LeistungArtikelZeile[] = [];
  let lohnListe: LeistungLohnZeile[] = [];
  for (const row of mergeable) {
    const typ = normalizePositionTyp(row.position_typ);
    if (typ === "artikel") {
      artikelListe.push(artikelPositionToLeistungZeile(row));
      continue;
    }
    const kalk = (row.kalkulation_json || { artikel_liste: [], lohn_liste: [] }) as LeistungKalkulation;
    artikelListe = [...artikelListe, ...(kalk.artikel_liste || [])];
    lohnListe = [...lohnListe, ...(kalk.lohn_liste || [])];
  }

  const totalNetto = mergeable.reduce((sum, row) => sum + positionTotals(row).zeilen_netto, 0);
  const mergedChildren = mergeable.map(snapshotMergedChild);
  const titleParts = mergeable
    .map((row) => (row.bezeichnung || "").trim())
    .filter(Boolean);

  let merged: EditorPosition = {
    localId: newLocalId("merged"),
    sort_index: firstIndex,
    position_typ: "leistung",
    bezeichnung: titleParts.length ? titleParts.join(" · ") : "Zusammengefasste Position",
    menge: 1,
    einzelpreis_netto: totalNetto,
    rabatt_prozent: 0,
    steuersatz_prozent: mergeable[0]?.steuersatz_prozent ?? 19,
    kalkulation_json: {
      artikel_liste: artikelListe,
      lohn_liste: lohnListe,
      vk_driver: "manuell",
    },
  };
  merged = patchPositionMeta(merged, { merged_children: mergedChildren });
  merged = syncLeistungEinzelpreis(merged);

  const removeIds = new Set(mergeable.map((row) => row.localId));
  const remaining = positions.filter((row) => !removeIds.has(row.localId));
  remaining.splice(firstIndex, 0, merged);
  return remaining.map((row, idx) => ({ ...row, sort_index: idx }));
}

export function appendPositionsToMergedLeistung(
  positions: EditorPosition[],
  mergedLocalId: string,
  selectedLocalIds: string[],
): EditorPosition[] {
  const mergedIndex = positions.findIndex((row) => row.localId === mergedLocalId);
  if (mergedIndex < 0) return positions;
  const merged = positions[mergedIndex];
  if (!merged || !isMergedContainerPosition(merged)) return positions;

  const selectedSet = new Set(selectedLocalIds);
  const toAdd = positions.filter(
    (row) =>
      selectedSet.has(row.localId) &&
      row.localId !== mergedLocalId &&
      isMergeablePosition(row) &&
      !isMergedContainerPosition(row),
  );
  if (!toAdd.length) return positions;

  const meta = getPositionMeta(merged);
  const existingChildren = [...(meta.merged_children || [])];
  const oldSum = calcMergedChildrenSum(existingChildren);
  const hadManualOverride = mergedPositionEpIsManual(merged, oldSum);

  const nextChildren = [...existingChildren, ...toAdd.map(snapshotMergedChild)];
  let updated = patchPositionMeta(merged, { merged_children: nextChildren });
  updated = reconcileMergedPositionFromChildren(updated);
  if (!hadManualOverride) {
    updated = assignMergedEinzelpreis(
      updated,
      calcMergedTargetEp(calcMergedChildrenSum(nextChildren), getPositionMeta(updated)),
    );
  }

  const removeIds = new Set(toAdd.map((row) => row.localId));
  const remaining = positions.filter((row) => !removeIds.has(row.localId));
  const nextMergedIndex = remaining.findIndex((row) => row.localId === mergedLocalId);
  if (nextMergedIndex < 0) return positions;
  remaining[nextMergedIndex] = updated;
  return remaining.map((row, idx) => ({ ...row, sort_index: idx }));
}

export function calcMergedChildrenSum(children: MergedChildSnapshot[]): number {
  return children.reduce((sum, child) => {
    const row = mergedChildToEditorPosition(child);
    const synced =
      normalizePositionTyp(child.position_typ) === "leistung" ? syncLeistungEinzelpreis(row) : row;
    return sum + positionTotals(synced).zeilen_netto;
  }, 0);
}

/** Interner Aufschlag (Prozent oder Euro) auf die Summe der Unterpositionen. */
export function calcMergedAufschlagAmount(childrenSum: number, meta: GdPositionMeta): number {
  if (normalizeAufschlagModus(meta.merged_aufschlag_modus) === "betrag") {
    return roundMoney(Number(meta.merged_aufschlag_betrag) || 0);
  }
  const pct = Number(meta.merged_aufschlag_prozent) || 0;
  if (!pct) return 0;
  return roundMoney(childrenSum * (pct / 100));
}

/** EP der zusammengefassten Position aus Unterpositionen und internem Aufschlag. */
export function calcMergedTargetEp(childrenSum: number, meta: GdPositionMeta): number {
  return roundMoney(childrenSum + calcMergedAufschlagAmount(childrenSum, meta));
}

const MERGED_EP_EPS = 0.005;

/** Wahr, wenn der EP bewusst von Summe + Aufschlag abweicht. */
export function mergedPositionEpIsManual(merged: EditorPosition, childrenSum: number): boolean {
  const target = calcMergedTargetEp(childrenSum, getPositionMeta(merged));
  return Math.abs(Number(merged.einzelpreis_netto ?? 0) - target) >= MERGED_EP_EPS;
}

function assignMergedEinzelpreis(merged: EditorPosition, ep: number): EditorPosition {
  const kalk = (merged.kalkulation_json || {
    artikel_liste: [],
    lohn_liste: [],
  }) as LeistungKalkulation;
  const value = roundMoney(Math.max(0, ep));
  return syncLeistungEinzelpreis({
    ...merged,
    einzelpreis_netto: value,
    kalkulation_json: {
      ...kalk,
      pauschale_netto: value,
      vk_driver: "manuell",
    },
  });
}

/** Setzt den EP der Zusammenfassung, ohne den Aufschlag zu verändern. */
export function setMergedPositionEinzelpreis(merged: EditorPosition, ep: number): EditorPosition {
  return assignMergedEinzelpreis(merged, ep);
}

/** Übernimmt einen internen Aufschlag und rechnet den EP neu, solange er nicht manuell ist. */
export function patchMergedPositionAufschlag(
  merged: EditorPosition,
  partial: Pick<
    GdPositionMeta,
    "merged_aufschlag_prozent" | "merged_aufschlag_betrag" | "merged_aufschlag_modus"
  >,
): EditorPosition {
  const children = getPositionMeta(merged).merged_children || [];
  const childrenSum = calcMergedChildrenSum(children);
  const manual = mergedPositionEpIsManual(merged, childrenSum);
  const next = patchPositionMeta(merged, partial);
  if (manual) return next;
  return assignMergedEinzelpreis(
    next,
    calcMergedTargetEp(childrenSum, getPositionMeta(next)),
  );
}

/** Hängt eine neue Artikel- oder Leistungsposition als Unterposition an. */
export function appendChildToMergedPosition(
  merged: EditorPosition,
  child: EditorPosition,
): EditorPosition {
  const existingChildren = [...(getPositionMeta(merged).merged_children || [])];
  const manual = mergedPositionEpIsManual(merged, calcMergedChildrenSum(existingChildren));
  const nextChildren = [...existingChildren, snapshotMergedChild(child)];
  let updated = patchPositionMeta(merged, { merged_children: nextChildren });
  updated = reconcileMergedPositionFromChildren(updated);
  if (manual) return updated;
  return assignMergedEinzelpreis(
    updated,
    calcMergedTargetEp(calcMergedChildrenSum(nextChildren), getPositionMeta(updated)),
  );
}

export function reconcileMergedPositionFromChildren(merged: EditorPosition): EditorPosition {
  const children = getPositionMeta(merged).merged_children;
  if (!children?.length) return merged;

  let artikelListe: LeistungArtikelZeile[] = [];
  let lohnListe: LeistungLohnZeile[] = [];
  for (const child of children) {
    const row = mergedChildToEditorPosition(child);
    const typ = normalizePositionTyp(child.position_typ);
    if (typ === "artikel") {
      artikelListe.push(artikelPositionToLeistungZeile(row));
      continue;
    }
    const kalk = (child.kalkulation_json || { artikel_liste: [], lohn_liste: [] }) as LeistungKalkulation;
    artikelListe = [...artikelListe, ...(kalk.artikel_liste || [])];
    lohnListe = [...lohnListe, ...(kalk.lohn_liste || [])];
  }

  const currentKalk = (merged.kalkulation_json || {}) as LeistungKalkulation;
  const next: EditorPosition = {
    ...merged,
    kalkulation_json: {
      ...currentKalk,
      artikel_liste: artikelListe,
      lohn_liste: lohnListe,
    },
  };
  return syncLeistungEinzelpreis(next);
}

export function updateMergedChildInPosition(
  merged: EditorPosition,
  childIndex: number,
  updatedChild: EditorPosition,
): EditorPosition {
  const meta = getPositionMeta(merged);
  const children = [...(meta.merged_children || [])];
  if (childIndex < 0 || childIndex >= children.length) return merged;

  const oldSum = calcMergedChildrenSum(children);
  const hadManualOverride = mergedPositionEpIsManual(merged, oldSum);

  children[childIndex] = snapshotMergedChild(updatedChild);
  let next = patchPositionMeta(merged, { merged_children: children });
  next = reconcileMergedPositionFromChildren(next);
  if (hadManualOverride) return next;

  return assignMergedEinzelpreis(
    next,
    calcMergedTargetEp(calcMergedChildrenSum(children), getPositionMeta(next)),
  );
}

export function dissolveMergedPosition(
  positions: EditorPosition[],
  mergedLocalId: string,
): EditorPosition[] {
  const mergedIndex = positions.findIndex((row) => row.localId === mergedLocalId);
  if (mergedIndex < 0) return positions;
  const merged = positions[mergedIndex];
  if (!merged) return positions;
  const children = getPositionMeta(merged).merged_children;
  if (!children?.length) return positions;

  const titelMeta = getPositionMeta(merged);
  const restored = children.map((child, offset) =>
    attachTitelFromMerged(restoreMergedChild(child, mergedIndex + offset), titelMeta),
  );
  const next = [...positions];
  next.splice(mergedIndex, 1, ...restored);
  return next.map((row, idx) => ({ ...row, sort_index: idx }));
}

/**
 * Entfernt eine Unterposition aus der Zusammenfassung und stellt sie als normale Position wieder her.
 * Bleiben weniger als 2 Unterpositionen übrig, wird die Zusammenfassung vollständig aufgelöst.
 */
export function extractMergedChildFromPositions(
  positions: EditorPosition[],
  mergedLocalId: string,
  childIndex: number,
): EditorPosition[] {
  const mergedIndex = positions.findIndex((row) => row.localId === mergedLocalId);
  if (mergedIndex < 0) return positions;
  const merged = positions[mergedIndex];
  if (!merged || !isMergedContainerPosition(merged)) return positions;

  const children = [...(getPositionMeta(merged).merged_children || [])];
  if (childIndex < 0 || childIndex >= children.length) return positions;

  // Weniger als 2 verbleibend → komplette Auflösung in Originalreihenfolge
  if (children.length - 1 < 2) {
    return dissolveMergedPosition(positions, mergedLocalId);
  }

  const [extractedSnapshot] = children.splice(childIndex, 1);
  if (!extractedSnapshot) return positions;

  const oldSum = calcMergedChildrenSum(getPositionMeta(merged).merged_children || []);
  const hadManualOverride = mergedPositionEpIsManual(merged, oldSum);

  let updated = patchPositionMeta(merged, { merged_children: children });
  updated = reconcileMergedPositionFromChildren(updated);
  if (!hadManualOverride) {
    updated = assignMergedEinzelpreis(
      updated,
      calcMergedTargetEp(calcMergedChildrenSum(children), getPositionMeta(updated)),
    );
  }

  const titelMeta = getPositionMeta(merged);
  const restored = attachTitelFromMerged(
    restoreMergedChild(extractedSnapshot, mergedIndex + 1),
    titelMeta,
  );

  const next = [...positions];
  next[mergedIndex] = updated;
  next.splice(mergedIndex + 1, 0, restored);
  return next.map((row, idx) => ({ ...row, sort_index: idx }));
}

function attachTitelFromMerged(row: EditorPosition, titelMeta: GdPositionMeta): EditorPosition {
  if (
    !titelMeta.struktur_titel_local_id &&
    titelMeta.struktur_titel_position_id == null &&
    titelMeta.struktur_titel_sort_index == null
  ) {
    return row;
  }
  return patchPositionMeta(row, {
    struktur_titel_local_id: titelMeta.struktur_titel_local_id ?? null,
    struktur_titel_position_id: titelMeta.struktur_titel_position_id ?? null,
    struktur_titel_sort_index: titelMeta.struktur_titel_sort_index ?? null,
  });
}

export function groupPositionsUnderTitel(
  positions: EditorPosition[],
  selectedLocalIds: string[],
): EditorPosition[] {
  if (selectedLocalIds.length < 1) return positions;

  const selectedSet = new Set(selectedLocalIds);
  const selectedRows = positions.filter((row) => selectedSet.has(row.localId));
  if (!selectedRows.length) return positions;

  const indices = selectedRows
    .map((row) => positions.findIndex((candidate) => candidate.localId === row.localId))
    .filter((index) => index >= 0)
    .sort((a, b) => a - b);
  const insertAt = indices[0] ?? positions.length;

  const titel = createBlockPosition("titel", insertAt);
  titel.bezeichnung = "Neuer Titel";
  const withTitel = insertPositionWithTitelAssignment(positions, titel, insertAt, undefined);

  return withTitel.map((row) => {
    if (!selectedSet.has(row.localId)) return row;
    if (normalizePositionTyp(row.position_typ) === "titel") return row;
    return patchPositionMeta(row, resolveTitelReference(titel.localId, withTitel));
  });
}
